'use client';

import { useEffect, useState } from 'react';

interface PriceData {
  priceId: string;
  amount: number;
  currency: string;
  interval?: string;
  displayAmount: string;
}

interface PricingResponse {
  success: boolean;
  prices: Record<string, PriceData>;
  timestamp: string;
}

interface PricingDisplayProps {
  /**
   * Custom API endpoint URL
   * Defaults to /api/public/stripe/prices
   */
  apiEndpoint?: string;

  /**
   * Custom className for styling
   */
  className?: string;

  /**
   * Render function for custom price display
   * If not provided, uses default rendering
   */
  renderPrice?: (key: string, price: PriceData) => React.ReactNode;

  /**
   * Show loading state
   */
  showLoading?: boolean;
}

/**
 * PricingDisplay Component
 *
 * Fetches and displays pricing information from Stripe via the API
 *
 * Usage:
 * ```tsx
 * <PricingDisplay />
 * ```
 *
 * With custom rendering:
 * ```tsx
 * <PricingDisplay
 *   renderPrice={(key, price) => (
 *     <div key={key}>
 *       <h3>{key}</h3>
 *       <p>{price.displayAmount}/{price.interval}</p>
 *     </div>
 *   )}
 * />
 * ```
 */
export default function PricingDisplay({
  apiEndpoint = '/api/public/stripe/prices',
  className = '',
  renderPrice,
  showLoading = true,
}: PricingDisplayProps) {
  // Use the usePricing hook instead of duplicating the fetch logic
  const { prices, loading, error } = usePricing(apiEndpoint);

  // Loading state
  if (loading && showLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-foreground-tertiary">Loading pricing information...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`p-6 bg-red-900/20 border border-red-500/30 rounded-lg ${className}`}>
        <p className="text-red-400">
          <strong>Error:</strong> {error}
        </p>
        <p className="text-foreground-tertiary text-sm mt-2">
          Please try again later or contact support if the problem persists.
        </p>
      </div>
    );
  }

  // No prices available
  if (!prices || Object.keys(prices).length === 0) {
    return (
      <div className={`p-6 bg-surface border border-border rounded-lg ${className}`}>
        <p className="text-foreground-secondary">No pricing information available at this time.</p>
      </div>
    );
  }

  // Custom rendering
  if (renderPrice) {
    return (
      <div className={className}>
        {Object.entries(prices).map(([key, price]) => renderPrice(key, price))}
      </div>
    );
  }

  // Default rendering
  return (
    <div className={`grid gap-4 ${className}`}>
      {Object.entries(prices).map(([key, price]) => (
        <div
          key={key}
          className="p-6 bg-surface border border-border rounded-lg hover:border-accent/40 transition-colors"
        >
          <div className="flex items-baseline justify-between">
            <div>
              <h3 className="text-xl font-bold text-foreground capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </h3>
              <p className="text-foreground-tertiary text-sm">Price ID: {price.priceId}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-accent">
                {price.displayAmount}
              </div>
              {price.interval && (
                <div className="text-foreground-secondary text-sm">
                  per {price.interval}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Hook to fetch pricing data
 *
 * Usage:
 * ```tsx
 * const { prices, loading, error } = usePricing();
 * ```
 */
export function usePricing(apiEndpoint: string = '/api/public/stripe/prices') {
  const [prices, setPrices] = useState<Record<string, PriceData> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPrices() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(apiEndpoint);

        if (!response.ok) {
          throw new Error(`Failed to fetch prices: ${response.statusText}`);
        }

        const data: PricingResponse = await response.json();

        if (data.success) {
          setPrices(data.prices);
        } else {
          throw new Error('Invalid response from pricing API');
        }
      } catch (err) {
        console.error('Error fetching prices:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pricing');
      } finally {
        setLoading(false);
      }
    }

    fetchPrices();
  }, [apiEndpoint]);

  return { prices, loading, error };
}
